/**
 * SF-INFRA-002 — Named route-path constants for the Storefront app.
 *
 * Callers navigate via these helpers instead of inlining `/products/…` or
 * `/account/…` strings so rename refactors stay mechanical:
 *
 *   navigate(storefrontRoutePaths.catalog.product(slug, pslug));
 *   <Link to={storefrontRoutePaths.account.orderDetail(id)} />;
 *
 * The `*Pattern` strings are the raw React Router paths used when *defining*
 * routes (with `:id` / `:slug` placeholders); the parameterised helpers
 * produce concrete URLs for navigation.
 *
 * Keep this file in sync with `router/index.tsx` — adding a route without a
 * matching entry here is a review blocker.
 */
export const storefrontRoutePaths = {
  // ──────────────────────────────────────── Public ────────────────────────────────────────
  home: '/',

  catalog: {
    categoryPattern: '/products/:categorySlug',
    category: (categorySlug: string) => `/products/${categorySlug}`,
    productPattern: '/products/:categorySlug/:productSlug',
    product: (categorySlug: string, productSlug: string) => `/products/${categorySlug}/${productSlug}`,
    /**
     * SF-NAV-001 — taxonomy-driven category browse route.
     *
     * `*` is a React Router splat that captures multi-segment paths like
     * `vehicle-wraps/cars`. The view reads the splat via `useParams()['*']`.
     */
    categoryBrowsePattern: '/category/*',
    categoryBrowse: (path: string) => `/category/${path}`,
  },

  /**
   * SF-CONFIG-003 — Phase 2 product type landing pages.
   *
   * `/product-types/:type-slug` renders the ProductTypeLandingView for each
   * Phase 2 product line (stickers, banners, business-print, etc.).
   *
   * Namespaced under `/product-types/` rather than `/products/` to avoid a
   * React Router score collision with `catalog.categoryPattern`
   * (`/products/:categorySlug`) — both are single-dynamic-segment routes and
   * React Router resolves them by registration order, which is fragile.
   * `/product-types/` is also consistent with the API path
   * `/api/v1/product-types/{type}/configurator`.
   *
   * Note: React Router v6 supports hyphens in param names; the segment is
   * accessed via `useParams()['type-slug']`.
   */
  configurator: {
    productTypeLandingPattern: '/product-types/:type-slug',
    productTypeLanding: (typeSlug: string) => `/product-types/${typeSlug}`,
    productTypeConfigurePattern: '/product-types/:type-slug/configure',
    productTypeConfigure: (typeSlug: string) => `/product-types/${typeSlug}/configure`,
  },

  /** Top-level product category landing (cascade-driven). */
  products: '/products',

  /** SF-QUOTE-103 — public quote-request page. Usable without an account. */
  quote: '/quote',
  /**
   * SF-QUOTE-103 — public, link-only access to a guest quote request via
   * its emailed access token. No account required to view; accepting a
   * sent quote gates on sign-in (see `QuoteAccessView`).
   */
  quoteAccessPattern: '/quotes/:token',
  quoteAccess: (token: string) => `/quotes/${token}`,

  about: '/about',
  howItWorks: '/how-it-works',
  fileRequirements: '/file-requirements',
  wholesale: '/wholesale',
  faq: '/faq',
  gallery: '/gallery',
  contact: '/contact',
  customDesign: '/custom-design',
  /** SF-DSVC-103 — CMS-managed page the "Designs" nav item points to. */
  designServices: '/design-services',
  terms: '/terms',
  privacy: '/privacy',
  shipping: '/shipping',
  returns: '/returns',
  installerProgram: '/installer-program',
  search: '/search',

  auth: {
    login: '/login',
    register: '/register',
    forgotPassword: '/forgot-password',
    resetPasswordPattern: '/reset-password/:token',
    resetPassword: (token: string) => `/reset-password/${token}`,
    verifyEmail: '/verify-email',
    restoreAccountPattern: '/restore-account/:token',
    restoreAccount: (token: string) => `/restore-account/${token}`,
  },

  /** System-level routes that render inside PublicLayout but outside the portal. */
  forbidden: '/403',

  // ──────────────────────────────────────── Customer portal ────────────────────────────────────────
  account: {
    root: '/account',
    profile: '/account/profile',
    security: '/account/security',
    orders: '/account/orders',
    orderDetailPattern: '/account/orders/:id',
    orderDetail: (id: string | number) => `/account/orders/${id}`,
    /**
     * SF-PROOFLOOP-010 — single deep-linked proof route shared by both
     * proof flows. `ProofRouterView` probes the generic PL-PROOFLOOP proof
     * endpoint and mounts `ProofView` (generic) or `ContourProofView`
     * (SF-CONTOUR-022, on a 404) — the two flows coexist by design.
     */
    proofPattern: '/account/orders/:orderNumber/proof',
    proof: (orderNumber: string) => `/account/orders/${orderNumber}/proof`,
    payBalancePattern: '/account/orders/:id/pay-balance',
    payBalance: (id: string | number) => `/account/orders/${id}/pay-balance`,
    artwork: '/account/artwork',
    paymentMethods: '/account/payment-methods',
    addresses: '/account/addresses',
    quotes: '/account/quotes',
    notifications: '/account/notifications',
    quoteNew: '/account/quotes/new',
    quoteDetailPattern: '/account/quotes/:id',
    quoteDetail: (id: string | number) => `/account/quotes/${id}`,
  },

  cart: '/cart',

  checkout: {
    root: '/checkout',
    confirmationPattern: '/checkout/confirmation/:id',
    confirmation: (id: string | number) => `/checkout/confirmation/${id}`,
    /**
     * Retired with the pre-authorization model (PL-DEPOSIT-009). The route is
     * kept so links in already-sent emails land somewhere useful; it redirects
     * to the order list, since the token that identified the order was resolved
     * by an endpoint that no longer exists.
     */
    reauthorizePattern: '/checkout/reauthorize/:token',
  },
} as const;

/** Helper type for callers that want to type-guard against the static keys. */
export type StorefrontRoutePaths = typeof storefrontRoutePaths;
